/**
 * Environments CRUD Page
 *
 * Full CRUD interface for managing environments in the BRUTAL config database.
 * Uses EITELKEIT tokens for styling.
 */

import { useEffect, useState, type CSSProperties } from 'react';
import { tokens } from '../local-components';
import {
  useAgent,
  type ConfigEnvironment,
  type EnvironmentCreateInput,
  type EnvironmentUpdateInput,
} from '../hooks/useAgent';

// ═══════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════

type EnvironmentTier = 'development' | 'staging' | 'production';

const ENVIRONMENT_TIERS: EnvironmentTier[] = ['development', 'staging', 'production'];

// ═══════════════════════════════════════════════════════════════
// STYLES
// ═══════════════════════════════════════════════════════════════

const pageStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: tokens.spacing[6],
};

const headerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

const titleStyle: CSSProperties = {
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes['2xl'],
  fontWeight: tokens.fontWeights.bold,
  color: tokens.colors.textPrimary,
  margin: 0,
};

const buttonStyle: CSSProperties = {
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes.sm,
  fontWeight: tokens.fontWeights.medium,
  color: tokens.colors.void,
  backgroundColor: tokens.colors.mint,
  border: 'none',
  borderRadius: tokens.radii.md,
  padding: `${tokens.spacing[2]} ${tokens.spacing[4]}`,
  cursor: 'pointer',
};

const buttonSecondaryStyle: CSSProperties = {
  ...buttonStyle,
  backgroundColor: 'transparent',
  color: tokens.colors.textPrimary,
  border: `1px solid ${tokens.colors.border}`,
};

const buttonDangerStyle: CSSProperties = {
  ...buttonStyle,
  backgroundColor: tokens.colors.danger,
  color: tokens.colors.textPrimary,
};

const buttonDisabledStyle: CSSProperties = {
  ...buttonStyle,
  backgroundColor: tokens.colors.surface,
  color: tokens.colors.textMuted,
  cursor: 'not-allowed',
};

const gridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
  gap: tokens.spacing[4],
};

const cardStyle: CSSProperties = {
  backgroundColor: tokens.colors.surface,
  border: `1px solid ${tokens.colors.border}`,
  borderRadius: tokens.radii.lg,
  padding: tokens.spacing[4],
  display: 'flex',
  flexDirection: 'column',
  gap: tokens.spacing[3],
};

const cardHeaderStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

const envNameStyle: CSSProperties = {
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes.lg,
  fontWeight: tokens.fontWeights.semibold,
  color: tokens.colors.textPrimary,
};

const tierBadgeStyle = (tier: string | null): CSSProperties => {
  const colors: Record<string, string> = {
    development: tokens.colors.info,
    staging: tokens.colors.warning,
    production: tokens.colors.mint,
  };
  const color = tier ? (colors[tier] ?? tokens.colors.textMuted) : tokens.colors.textMuted;

  return {
    fontFamily: tokens.fonts.mono,
    fontSize: tokens.fontSizes.xs,
    fontWeight: tokens.fontWeights.medium,
    color: color,
    backgroundColor: `${color}22`,
    padding: `2px ${tokens.spacing[2]}`,
    borderRadius: tokens.radii.sm,
    textTransform: 'uppercase',
  };
};

const descStyle: CSSProperties = {
  fontFamily: tokens.fonts.sans,
  fontSize: tokens.fontSizes.sm,
  color: tokens.colors.textMuted,
  lineHeight: 1.5,
};

const rowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: tokens.spacing[2],
};

const labelStyle: CSSProperties = {
  fontFamily: tokens.fonts.sans,
  fontSize: tokens.fontSizes.xs,
  fontWeight: tokens.fontWeights.medium,
  color: tokens.colors.textMuted,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  minWidth: '80px',
};

const valueStyle: CSSProperties = {
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes.sm,
  color: tokens.colors.textPrimary,
};

const cardActionsStyle: CSSProperties = {
  display: 'flex',
  gap: tokens.spacing[2],
  marginTop: tokens.spacing[2],
  paddingTop: tokens.spacing[3],
  borderTop: `1px solid ${tokens.colors.border}`,
};

const actionButtonStyle: CSSProperties = {
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes.xs,
  color: tokens.colors.textMuted,
  backgroundColor: 'transparent',
  border: `1px solid ${tokens.colors.border}`,
  borderRadius: tokens.radii.sm,
  cursor: 'pointer',
  padding: `${tokens.spacing[1]} ${tokens.spacing[2]}`,
};

const loadingStyle: CSSProperties = {
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes.sm,
  color: tokens.colors.textMuted,
  padding: tokens.spacing[6],
  textAlign: 'center',
};

const errorStyle: CSSProperties = {
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes.sm,
  color: tokens.colors.danger,
  padding: tokens.spacing[4],
  backgroundColor: 'rgba(255, 71, 87, 0.1)',
  border: `1px solid ${tokens.colors.danger}`,
  borderRadius: tokens.radii.md,
};

const emptyStyle: CSSProperties = {
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes.sm,
  color: tokens.colors.textMuted,
  textAlign: 'center',
  padding: tokens.spacing[8],
  backgroundColor: tokens.colors.surface,
  border: `1px dashed ${tokens.colors.border}`,
  borderRadius: tokens.radii.lg,
};

// Modal styles
const modalOverlayStyle: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.7)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};

const modalStyle: CSSProperties = {
  backgroundColor: tokens.colors.surface,
  border: `1px solid ${tokens.colors.border}`,
  borderRadius: tokens.radii.lg,
  padding: tokens.spacing[6],
  width: '500px',
  maxWidth: '90vw',
  maxHeight: '90vh',
  overflow: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: tokens.spacing[4],
};

const modalTitleStyle: CSSProperties = {
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes.lg,
  fontWeight: tokens.fontWeights.semibold,
  color: tokens.colors.textPrimary,
  margin: 0,
};

const formGroupStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: tokens.spacing[2],
};

const inputStyle: CSSProperties = {
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes.sm,
  color: tokens.colors.textPrimary,
  backgroundColor: tokens.colors.void,
  border: `1px solid ${tokens.colors.border}`,
  borderRadius: tokens.radii.md,
  padding: `${tokens.spacing[2]} ${tokens.spacing[3]}`,
  outline: 'none',
};

const selectStyle: CSSProperties = {
  ...inputStyle,
  cursor: 'pointer',
};

const textareaStyle: CSSProperties = {
  ...inputStyle,
  minHeight: '80px',
  resize: 'vertical',
};

const modalActionsStyle: CSSProperties = {
  display: 'flex',
  gap: tokens.spacing[2],
  justifyContent: 'flex-end',
  marginTop: tokens.spacing[2],
};

const variablesContainerStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: tokens.spacing[1],
};

const variableTagStyle: CSSProperties = {
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes.xs,
  color: tokens.colors.textMuted,
  backgroundColor: tokens.colors.void,
  border: `1px solid ${tokens.colors.border}`,
  borderRadius: tokens.radii.sm,
  padding: `2px ${tokens.spacing[2]}`,
};

// ═══════════════════════════════════════════════════════════════
// MODAL COMPONENTS
// ═══════════════════════════════════════════════════════════════

interface EnvironmentFormProps {
  environment: ConfigEnvironment | null;
  onSave: (data: EnvironmentCreateInput | EnvironmentUpdateInput, isEdit: boolean) => Promise<void>;
  onClose: () => void;
  isSaving: boolean;
}

function EnvironmentForm({ environment, onSave, onClose, isSaving }: EnvironmentFormProps) {
  const isEdit = environment !== null;
  const [name, setName] = useState(environment?.name ?? '');
  const [description, setDescription] = useState(environment?.description ?? '');
  const [tier, setTier] = useState<EnvironmentTier | ''>(environment?.tier ?? '');
  const [region, setRegion] = useState(environment?.region ?? '');
  const [variablesJson, setVariablesJson] = useState(
    environment?.variables ? JSON.stringify(environment.variables, null, 2) : '{}',
  );
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setError(null);

    let variables: Record<string, string> | null = null;
    try {
      const parsed = JSON.parse(variablesJson);
      if (typeof parsed === 'object' && parsed !== null) {
        variables = parsed as Record<string, string>;
      }
    } catch {
      setError('Invalid JSON in variables field');
      return;
    }

    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    if (isEdit) {
      const updates: EnvironmentUpdateInput = {
        description: description !== environment.description ? description || null : null,
        tier: tier !== environment.tier ? (tier as EnvironmentTier) || null : null,
        region: region !== environment.region ? region || null : null,
        variables:
          JSON.stringify(variables) !== JSON.stringify(environment.variables) ? variables : null,
        secrets: null,
        approvals: null,
        constraints: null,
        notifications: null,
        metadata: null,
      };
      await onSave(updates, true);
    } else {
      const data: EnvironmentCreateInput = {
        name: name.trim(),
        description: description || null,
        tier: (tier as EnvironmentTier) || null,
        region: region || null,
        variables,
        secrets: null,
        approvals: null,
        constraints: null,
        notifications: null,
        metadata: null,
      };
      await onSave(data, false);
    }
  };

  return (
    <div style={modalOverlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <h3 style={modalTitleStyle}>{isEdit ? 'Edit Environment' : 'Create Environment'}</h3>

        <div style={formGroupStyle}>
          <label style={labelStyle}>Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="production"
            style={inputStyle}
            disabled={isEdit}
          />
        </div>

        <div style={formGroupStyle}>
          <label style={labelStyle}>Description</label>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Production environment"
            style={inputStyle}
          />
        </div>

        <div style={formGroupStyle}>
          <label style={labelStyle}>Tier</label>
          <select
            value={tier}
            onChange={(e) => setTier(e.target.value as EnvironmentTier | '')}
            style={selectStyle}
          >
            <option value="">Select tier...</option>
            {ENVIRONMENT_TIERS.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>

        <div style={formGroupStyle}>
          <label style={labelStyle}>Region</label>
          <input
            type="text"
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            placeholder="eu-west-1"
            style={inputStyle}
          />
        </div>

        <div style={formGroupStyle}>
          <label style={labelStyle}>Variables (JSON)</label>
          <textarea
            value={variablesJson}
            onChange={(e) => setVariablesJson(e.target.value)}
            placeholder='{"KEY": "value"}'
            style={textareaStyle}
          />
        </div>

        {error !== null && <div style={errorStyle}>{error}</div>}

        <div style={modalActionsStyle}>
          <button onClick={onClose} style={buttonSecondaryStyle} disabled={isSaving}>
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            style={isSaving ? buttonDisabledStyle : buttonStyle}
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : isEdit ? 'Update' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}

interface DeleteConfirmProps {
  envName: string;
  onConfirm: () => Promise<void>;
  onClose: () => void;
  isDeleting: boolean;
}

function DeleteConfirm({ envName, onConfirm, onClose, isDeleting }: DeleteConfirmProps) {
  return (
    <div style={modalOverlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <h3 style={modalTitleStyle}>Delete Environment</h3>
        <p
          style={{
            fontFamily: tokens.fonts.sans,
            fontSize: tokens.fontSizes.sm,
            color: tokens.colors.textPrimary,
          }}
        >
          Are you sure you want to delete <strong>{envName}</strong>? This action cannot be undone.
        </p>
        <div style={modalActionsStyle}>
          <button onClick={onClose} style={buttonSecondaryStyle} disabled={isDeleting}>
            Cancel
          </button>
          <button
            onClick={onConfirm}
            style={isDeleting ? buttonDisabledStyle : buttonDangerStyle}
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ENVIRONMENT CARD
// ═══════════════════════════════════════════════════════════════

interface EnvironmentCardProps {
  env: ConfigEnvironment;
  onEdit: () => void;
  onDelete: () => void;
}

function EnvironmentCard({ env, onEdit, onDelete }: EnvironmentCardProps) {
  const variableKeys = env.variables ? Object.keys(env.variables) : [];

  return (
    <div style={cardStyle}>
      <div style={cardHeaderStyle}>
        <span style={envNameStyle} data-testid="environment-name">
          {env.name}
        </span>
        <span style={tierBadgeStyle(env.tier)}>{env.tier ?? 'unset'}</span>
      </div>

      {env.description && <p style={descStyle}>{env.description}</p>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacing[2] }}>
        {env.region && (
          <div style={rowStyle}>
            <span style={labelStyle}>Region</span>
            <span style={valueStyle}>{env.region}</span>
          </div>
        )}
        <div style={rowStyle}>
          <span style={labelStyle}>Variables</span>
          <span style={valueStyle}>{variableKeys.length}</span>
        </div>
        {variableKeys.length > 0 && (
          <div style={variablesContainerStyle}>
            {variableKeys.slice(0, 5).map((key) => (
              <span key={key} style={variableTagStyle}>
                {key}
              </span>
            ))}
            {variableKeys.length > 5 && (
              <span style={{ ...variableTagStyle, color: tokens.colors.textMuted }}>
                +{variableKeys.length - 5} more
              </span>
            )}
          </div>
        )}
      </div>

      <div style={cardActionsStyle}>
        <button onClick={onEdit} style={actionButtonStyle}>
          Edit
        </button>
        <button
          onClick={onDelete}
          style={{
            ...actionButtonStyle,
            color: tokens.colors.danger,
            borderColor: tokens.colors.danger,
          }}
        >
          Delete
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════

export function EnvironmentsCrud() {
  const { isConnected, listEnvironments, createEnvironment, updateEnvironment, deleteEnvironment } =
    useAgent();
  const [environments, setEnvironments] = useState<ConfigEnvironment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal state
  const [showForm, setShowForm] = useState(false);
  const [editingEnv, setEditingEnv] = useState<ConfigEnvironment | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchEnvironments = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await listEnvironments();
      setEnvironments(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch environments');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isConnected) {
      fetchEnvironments();
    }
  }, [isConnected]);

  const handleCreate = () => {
    setEditingEnv(null);
    setShowForm(true);
  };

  const handleEdit = (env: ConfigEnvironment) => {
    setEditingEnv(env);
    setShowForm(true);
  };

  const handleSave = async (
    data: EnvironmentCreateInput | EnvironmentUpdateInput,
    isEdit: boolean,
  ) => {
    setIsSaving(true);
    setError(null);
    try {
      if (isEdit && editingEnv !== null) {
        const result = await updateEnvironment(editingEnv.name, data as EnvironmentUpdateInput);
        if (!result.success) {
          setError(result.error ?? 'Update failed');
          setIsSaving(false);
          return;
        }
      } else {
        const result = await createEnvironment(data as EnvironmentCreateInput);
        if (!result.success) {
          setError(result.error ?? 'Create failed');
          setIsSaving(false);
          return;
        }
      }
      setShowForm(false);
      setEditingEnv(null);
      await fetchEnvironments();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Operation failed');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (deleteTarget === null) return;
    setIsDeleting(true);
    try {
      const result = await deleteEnvironment(deleteTarget);
      if (!result.success) {
        setError(result.error ?? 'Delete failed');
      } else {
        setDeleteTarget(null);
        await fetchEnvironments();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingEnv(null);
  };

  if (!isConnected) {
    return (
      <div style={pageStyle}>
        <h1 style={titleStyle}>Environments (Config)</h1>
        <div style={loadingStyle}>Connecting to agent...</div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div style={pageStyle}>
        <h1 style={titleStyle}>Environments (Config)</h1>
        <div style={loadingStyle}>Loading environments...</div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <h1 style={titleStyle}>Environments (Config)</h1>
        <div style={{ display: 'flex', gap: tokens.spacing[2] }}>
          <button onClick={fetchEnvironments} style={buttonSecondaryStyle}>
            Refresh
          </button>
          <button onClick={handleCreate} style={buttonStyle} data-testid="create-environment-btn">
            + Create Environment
          </button>
        </div>
      </div>

      {/* Error */}
      {error !== null && <div style={errorStyle}>Error: {error}</div>}

      {/* Environments Grid */}
      {environments.length === 0 ? (
        <div style={emptyStyle}>
          No environments defined. Click "Create Environment" to add one.
        </div>
      ) : (
        <div style={gridStyle}>
          {environments.map((env) => (
            <EnvironmentCard
              key={env.id}
              env={env}
              onEdit={() => handleEdit(env)}
              onDelete={() => setDeleteTarget(env.name)}
            />
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showForm && (
        <EnvironmentForm
          environment={editingEnv}
          onSave={handleSave}
          onClose={handleCloseForm}
          isSaving={isSaving}
        />
      )}

      {/* Delete Confirmation */}
      {deleteTarget !== null && (
        <DeleteConfirm
          envName={deleteTarget}
          onConfirm={handleDelete}
          onClose={() => setDeleteTarget(null)}
          isDeleting={isDeleting}
        />
      )}
    </div>
  );
}
